/**
 * Store centralizzato - sempre backend (database SQLite)
 * Nessun fallback localStorage: un'unica fonte di verità per evitare corruzione dati.
 */

import {
  ABSENCE_CATEGORIES,
  DESIDERATA_TYPES,
  DEFAULT_WEEKLY_HOURS,
  HOURS_PER_SLOT,
  HOURS_PER_DAY,
  WEEKDAYS,
  DISTRIBUTION_AXIS_ORDER,
  DISTRIBUTION_KEYS,
  defaultDistribuzioneFiveAxes,
  getTemplateWeekdays,
  sortWeekdaysCalendarOrder,
  CATEGORY_TO_UOC,
} from './types.js';
import * as api from './api.js';
import {
  getFestiviInfrasettimanali,
  ensurePlannerSlotName,
  addCalendarDays,
  slotLogicalKeyString,
  stableCalendarSlotKey,
} from './utils.js';

/** Solo preferenza UI Piano turni (non dati turno). Default: disattivo. */
const LS_AUTO_LOCK_MANUAL_ASSIGNMENTS = 'turni:autoLockManualAssignments';

export function getAutoLockManualAssignments() {
  try {
    const v = localStorage.getItem(LS_AUTO_LOCK_MANUAL_ASSIGNMENTS);
    if (v === '1' || v === 'true') return true;
    return false;
  } catch (_) {
    return false;
  }
}

export function setAutoLockManualAssignments(enabled) {
  try {
    localStorage.setItem(LS_AUTO_LOCK_MANUAL_ASSIGNMENTS, enabled ? '1' : '0');
  } catch (_) {}
}

/** Utente corrente (da sessionStorage dopo login) */
export function getCurrentUser() {
  return api.getStoredUser();
}

/** Admin/director possono gestire medici */
export function canEditDoctors() {
  return (state.currentUser?.role || '').match(/^(admin|director)$/);
}

export function canEditCalendar() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

export function canEditShifts() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Solo admin e direttore possono archiviare/sbloccare settimane (approvazione definitiva). */
export function canLockWeekArchives() {
  return (state.currentUser?.role || '').match(/^(admin|director)$/);
}

/** Admin, organizer, director: possono esportare PDF richieste assenze/desiderata */
export function canExportAbsencesRequestsPdf() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Admin, organizer, director: vedono il saldo bimestrale dei colleghi nelle card del Piano turni.
 *  Il medico no: è il numero da cui dipendono le ritenute, e nella sua Home ha già il proprio. */
export function canSeeWorkloadBimesterBalances() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Riga «Assenze/Desiderata» nel piè della griglia Piano Turni: la vedono tutti i ruoli.
 *  Al medico serve per sapere su chi può contare nei turni che lo riguardano. */
export function canSeePlannerAbsenceRow() {
  return Boolean(state.currentUser?.role);
}

/** Admin, organizer, director: nella riga «Assenze/Desiderata» vedono anche la libera
 *  professione dei colleghi (dato sensibile, fuori servizio) e possono modificare le voci. */
export function canManagePlannerAbsenceRow() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Admin, organizer, director: possono esportare il resoconto PDF del bimestre in Carichi. */
export function canExportWorkloadBimesterPdf() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Admin/organizer/director: possono bloccare/sbloccare settimane per richieste assenze/desiderata (effetto solo su medici). */
export function canManageAbsenceRequestLocks() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** Può modificare le assenze per un dato medico (admin/organizer/director per tutti, user solo per il proprio; dateStr = giorno per controllo blocco settimanale). */
export function canEditAbsencesFor(doctorId, dateStr) {
  const role = state.currentUser?.role;
  if (role === 'admin' || role === 'organizer' || role === 'director') return true;
  if (role === 'user') {
    if (state.currentUser?.doctorId !== doctorId) return false;
    if (dateStr && state.absenceRequestLockedWeeks?.[getWeekStart(dateStr)]) return false;
    return true;
  }
  return false;
}

export function canManageUsers() {
  return state.currentUser?.role === 'admin';
}

export function canManageBoard() {
  return (state.currentUser?.role || '').match(/^(admin|organizer|director)$/);
}

/** True se doctorId è il medico collegato all'utente corrente (per ruolo user) */
export function isOwnDoctor(doctorId) {
  return state.currentUser?.doctorId === doctorId;
}

function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const daysToMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysToMonday);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Forma compatibile con `slotLogicalKeyString` da riga API / stato. */
function _slotShapeForLogicalKey(s) {
  if (!s) return null;
  return {
    date: s.date,
    timeSlot: s.timeSlot ?? s.time_slot,
    category: s.category,
    activityType: s.activityType ?? s.activity_type,
    uocId: s.uocId ?? s.uoc_id,
    name: s.name,
  };
}

/** True se uno slot Piano risulta bloccato in stato usando anche chiave logica (es. migrazione -tpl → lgk). */
function _shiftPlanSlotLockedViaState(slot, lockedByKey, stateSlots, autoUnlockedSet) {
  const key = slot.slotKey || slot.id;
  if (autoUnlockedSet.has(key) || (slot.id && autoUnlockedSet.has(slot.id))) return false;
  if (lockedByKey[key] || (slot.id && lockedByKey[slot.id])) return true;
  const lk0 = slotLogicalKeyString(slot);
  if (!lk0?.length || !stateSlots?.length) return false;
  for (const rs of stateSlots) {
    if (!rs?.date) continue;
    const lk1 = slotLogicalKeyString(_slotShapeForLogicalKey(rs));
    if (lk1 !== lk0) continue;
    const kr = rs.slotKey || rs.id;
    if (kr && lockedByKey[kr]) return true;
  }
  return false;
}

/** Risolve assignment in stato quando `shiftAssignmentsByKey` è ancora sul vecchio `slot_key`/id. */
function _shiftPlanAssignmentViaState(slot, assignmentsByKey, stateSlots) {
  const key = slot.slotKey || slot.id;
  let v = assignmentsByKey[key];
  if (v) return v;
  if (slot.id && assignmentsByKey[slot.id]) return assignmentsByKey[slot.id];
  const lk0 = slotLogicalKeyString(slot);
  if (!lk0?.length || !stateSlots?.length) return undefined;
  for (const rs of stateSlots) {
    const lk1 = slotLogicalKeyString(_slotShapeForLogicalKey(rs));
    if (lk1 !== lk0) continue;
    const kr = rs.slotKey || rs.id;
    if (kr && assignmentsByKey[kr]) return assignmentsByKey[kr];
  }
  return undefined;
}

/** Normalizza riga modifica calendario da API (snake_case o camelCase). */
export function normalizeCalendarModificationRow(m) {
  if (!m) return m;
  return {
    id: m.id,
    modificationType: m.modificationType ?? m.modification_type ?? '',
    scope: m.scope ?? '',
    uocId: m.uocId ?? m.uoc_id ?? '',
    weekday: m.weekday ?? '',
    dateSpecific: m.dateSpecific ?? m.date_specific ?? '',
    dateFrom: m.dateFrom ?? m.date_from ?? '',
    dateTo: m.dateTo ?? m.date_to ?? '',
    timeSlot: m.timeSlot ?? m.time_slot ?? '',
    category: m.category ?? '',
    activityType: m.activityType ?? m.activity_type ?? 'clinico',
    name: m.name ?? '',
  };
}

/** Normalizza riga template da API o default (weekdays[], dateFrom/dateTo). */
export function normalizeCalendarTemplateRow(t) {
  if (!t) return t;
  const raw = getTemplateWeekdays(t);
  let weekdays = sortWeekdaysCalendarOrder(raw);
  if (!weekdays.length) weekdays = ['lun'];
  return {
    ...t,
    weekdays,
    weekday: t.weekday || weekdays[0],
    dateFrom: t.dateFrom ?? null,
    dateTo: t.dateTo ?? null,
  };
}

/** Template calendario predefiniti: una riga = più giorni settimanali + fascia. */
function getDefaultCalendarTemplates() {
  const lunVen = ['lun', 'mar', 'mer', 'gio', 'ven'];
  const tutti = [...WEEKDAYS];
  const rows = [];
  const push = (name, category, uocId, wdList, timeSlot, activityType = 'clinico') => {
    const weekdays = sortWeekdaysCalendarOrder(wdList);
    rows.push({
      id: generateId(),
      name,
      category,
      uocId,
      weekdays,
      weekday: weekdays[0] || 'lun',
      timeSlot,
      activityType,
      dateFrom: null,
      dateTo: null,
    });
  };
  push('TI', 'terapia_intensiva', 'uoc_anestesia_ti', tutti, 'mattino');
  push('TI', 'terapia_intensiva', 'uoc_anestesia_ti', tutti, 'pomeriggio');
  push('TI', 'terapia_intensiva', 'uoc_anestesia_ti', tutti, 'notte');
  push('SO1', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'mattino');
  push('SO1', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'pomeriggio');
  push('SO2', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'mattino');
  push('SO2', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'pomeriggio');
  push('SO3', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'mattino');
  push('SO3', 'sala_operatoria', 'uos_blocco_operatorio', lunVen, 'pomeriggio');
  push('RMN', 'nora', 'uos_blocco_operatorio', lunVen, 'mattino');
  push('Amb Anest', 'sala_operatoria', 'uos_blocco_operatorio', ['lun', 'mar', 'gio'], 'mattino', 'non_clinico');
  push('MedDol', 'medicina_dolore', 'uos_medicina_dolore', ['lun', 'mer', 'ven'], 'mattino');
  push('MedDol', 'medicina_dolore', 'uos_medicina_dolore', ['mar', 'gio'], 'pomeriggio');
  push('CP', 'cure_palliative', 'uos_medicina_dolore', ['lun', 'mer', 'ven'], 'mattino');
  push('MedDol2', 'medicina_dolore', 'uos_medicina_dolore', ['mar', 'gio'], 'pomeriggio');
  push('Form', 'formazione', 'uoc_anestesia_ti', ['mar', 'gio'], 'mattino', 'non_clinico');
  push('Direzione', 'direzione', 'uoc_anestesia_ti', lunVen, 'prolungato', 'direzione');
  push('Rep Notte', 'sala_operatoria', 'uos_blocco_operatorio', tutti, 'reperibilita_notte', 'reperibilita');
  push('Rep Giorno', 'sala_operatoria', 'uos_blocco_operatorio', ['sab', 'dom'], 'reperibilita_giorno', 'reperibilita');
  return rows;
}

function generateId() {
  return crypto.randomUUID?.() || `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getDefaultState() {
  return {
    currentUser: api.getStoredUser(),
    doctors: [],
    absences: [],
    desiderata: [],
    shiftSlots: [],
    shiftAssignments: {},
    shiftAssignmentsByKey: {}, // slotKey -> doctorId (per Piano Turni da Calendario)
    lockedAssignments: {}, // slotId -> true
    lockedAssignmentsByKey: {}, // slotKey -> true
    calendarTemplates: getDefaultCalendarTemplates().map(normalizeCalendarTemplateRow),
    calendarModifications: [],
    uocs: [],
    currentMonth: new Date().toISOString().slice(0, 7),
    shiftPlanWeekFrom: null,
    shiftPlanWeekTo: null,
    workloadBimester: null,
    workloadAnnualYear: null,
    absencesCalendarWeekFrom: null,
    absencesCalendarWeekTo: null,
    calendarWeekStart: null,
    workloadHistory: {}, // "YYYY-MM" -> { doctorId: { feriali, weekend, notturne, reperibilita, total } }
    workloadBimesterSnapshots: {}, // "YYYY-Bn" -> { data: { doctorId: {...} }, createdAt, createdBy }
    lockedWeeks: {}, // weekStart (YYYY-MM-DD) -> true
    absenceRequestLockedWeeks: {}, // weekStart (lunedì) -> true — blocca solo utenti medico su assenze/desiderata
    shiftImportIssues: [], // warning ultimo import XLSX dell'utente corrente
    recurringExclusions: [],
    recurringOverrides: [], // { recurringId, date }
    boardUnreadCount: 0,
  };
}

/** Normalizza somma percentuali a 100 sulle 5 chiavi distribuzione. */
export function normalizeDistribuzioneFive(dist) {
  const keys = DISTRIBUTION_AXIS_ORDER;
  const base = {};
  keys.forEach(k => {
    base[k] = Math.max(0, Math.min(100, Number(dist?.[k]) || 0));
  });
  let sum = keys.reduce((a, k) => a + base[k], 0);
  if (sum <= 0) return defaultDistribuzioneFiveAxes();
  let rem = 100;
  keys.slice(0, -1).forEach(k => {
    const v = Math.round((base[k] / sum) * 100);
    base[k] = v;
    rem -= v;
  });
  base[keys[keys.length - 1]] = Math.max(0, rem);
  return base;
}

/** Migra modello 4 assi (TI, SO, MD, Dir) → 5 assi; merge Dir in TI. */
export function migrateDoctorDistribuzione(raw) {
  let d = raw;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      d = {};
    }
  }
  if (!d || typeof d !== 'object') d = {};
  const hasAllFive = DISTRIBUTION_AXIS_ORDER.every(k => d[k] !== undefined && d[k] !== null && !Number.isNaN(Number(d[k])));
  if (hasAllFive) return normalizeDistribuzioneFive(d);
  const ti = Number(d.terapia_intensiva) || 0;
  const so = Number(d.sala_operatoria) || 0;
  const md = Number(d.medicina_dolore) || 0;
  const dir = Number(d.direzione) || 0;
  const sumOld = ti + so + md + dir;
  if (sumOld <= 0) return defaultDistribuzioneFiveAxes();
  const mdA = Math.round(md / 2);
  const mdB = md - mdA;
  return normalizeDistribuzioneFive({
    [DISTRIBUTION_KEYS.TERAPIA_INTENSIVA]: ti + dir,
    [DISTRIBUTION_KEYS.FORMAZIONE]: 0,
    [DISTRIBUTION_KEYS.SALA_OPERATORIA]: so,
    [DISTRIBUTION_KEYS.MEDICINA_DOLORE]: mdA,
    [DISTRIBUTION_KEYS.CURE_PALLIATIVE]: mdB,
  });
}

function normalizeApiDoctor(d) {
  let dist = d.distribuzione;
  if (typeof dist === 'string') {
    try {
      dist = JSON.parse(dist);
    } catch {
      dist = {};
    }
  }
  return {
    id: d.id,
    nome: d.nome,
    cognome: d.cognome,
    sigla: d.sigla,
    debitoOrarioSettimanale: d.debitoOrarioSettimanale ?? d.debito_orario_settimanale ?? DEFAULT_WEEKLY_HOURS,
    esenzioneNotti: d.esenzioneNotti ?? Boolean(d.esenzione_notti),
    esenzioneWeekend: d.esenzioneWeekend ?? Boolean(d.esenzione_weekend),
    esenzioneAttivitaCliniche: d.esenzioneAttivitaCliniche ?? Boolean(d.esenzione_attivita_cliniche),
    dataFineRapporto: d.dataFineRapporto ?? d.data_fine_rapporto ?? null,
    abilitaDirezione: d.abilitaDirezione ?? Boolean(d.abilita_direzione),
    specializzando: Boolean(d.specializzando ?? 0),
    saldoOreApertura: Number(d.saldoOreApertura ?? d.saldo_ore_apertura ?? 0) || 0,
    saldoOreAperturaBimestre: d.saldoOreAperturaBimestre ?? d.saldo_ore_apertura_bimestre ?? null,
    distribuzione: migrateDoctorDistribuzione(dist || {}),
  };
}

function normalizeApiAbsence(a) {
  return {
    id: a.id,
    doctorId: a.doctorId ?? a.doctor_id,
    dateStart: a.dateStart ?? a.date_start,
    dateEnd: a.dateEnd ?? a.date_end,
    category: a.category,
    slotTypes: a.slotTypes ?? (a.slot_types ? (typeof a.slot_types === 'string' ? JSON.parse(a.slot_types || '[]') : a.slot_types) : []),
    oreFormazione: a.oreFormazione ?? a.ore_formazione ?? 0,
  };
}

function normalizeApiSlot(s) {
  return {
    id: s.id,
    slotKey: s.slotKey ?? s.slot_key,
    name: s.name,
    category: s.category,
    activityType: s.activityType ?? s.activity_type ?? 'clinico',
    timeSlot: s.timeSlot ?? s.time_slot,
    date: s.date,
    weekday: s.weekday,
    uocId: s.uocId ?? s.uoc_id,
    plannerCellKey: s.plannerCellKey ?? s.planner_cell_key ?? '',
  };
}

let state = getDefaultState();
const listeners = new Set();

export function getState() {
  return { ...state };
}

let stateBatchDepth = 0;
let stateBatchPendingNotify = false;

export function setState(updater) {
  state = typeof updater === 'function' ? updater(state) : { ...updater };
  if (stateBatchDepth > 0) {
    stateBatchPendingNotify = true;
    return;
  }
  listeners.forEach(fn => fn(state));
}

/** Raggruppa più setState in un’unica notifica ai subscriber (evita re-render a cascata). */
export function beginStateBatch() {
  stateBatchDepth += 1;
}

/**
 * @param {{ notify?: boolean }} [opts] - se notify=false, non avvisa i listener (il chiamante aggiorna la UI).
 */
export function endStateBatch(opts = {}) {
  const notify = opts.notify !== false;
  stateBatchDepth = Math.max(0, stateBatchDepth - 1);
  if (stateBatchDepth > 0) return;
  if (!stateBatchPendingNotify) return;
  stateBatchPendingNotify = false;
  if (notify) listeners.forEach(fn => fn(state));
}

/**
 * Esegue fn con setState senza notify intermedi.
 * @param {() => (void|Promise<void>)} fn
 * @param {{ notify?: boolean }} [opts]
 */
export async function withStateBatch(fn, opts = {}) {
  beginStateBatch();
  try {
    return await fn();
  } finally {
    endStateBatch(opts);
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True se il backend è connesso (l'app funziona solo in questo caso) */
export function isBackendConnected() {
  return state._backendConnected === true;
}

/** Unisce automaticamente le assenze duplicate e contigue (stesso medico, stessa categoria). */
async function mergeDuplicateAbsences() {
  const absences = state.absences;
  const byDoctorCat = new Map();
  for (const a of absences) {
    const key = `${a.doctorId}|${a.category}`;
    if (!byDoctorCat.has(key)) byDoctorCat.set(key, []);
    byDoctorCat.get(key).push({ ...a });
  }
  const toDelete = [];
  const toCreate = [];
  for (const [, group] of byDoctorCat) {
    const sorted = group.sort((x, y) => x.dateStart.localeCompare(y.dateStart));
    let i = 0;
    while (i < sorted.length) {
      const curr = sorted[i];
      let start = curr.dateStart;
      let end = curr.dateEnd || curr.dateStart;
      let j = i + 1;
      while (j < sorted.length) {
        const next = sorted[j];
        const nextStart = next.dateStart;
        const gapEnd = new Date(end + 'T12:00:00');
        gapEnd.setDate(gapEnd.getDate() + 1);
        const nextDay = gapEnd.toISOString().slice(0, 10);
        if (nextStart <= nextDay) {
          const nextEnd = next.dateEnd || next.dateStart;
          if (nextEnd > end) end = nextEnd;
          toDelete.push(next);
          j++;
        } else break;
      }
      if (j > i + 1) {
        toDelete.push(curr);
        toCreate.push({
          doctorId: curr.doctorId,
          dateStart: start,
          dateEnd: end,
          category: curr.category,
          slotTypes: curr.slotTypes || [],
          oreFormazione: curr.oreFormazione ?? 0,
        });
      }
      i = j;
    }
  }
  for (const a of toDelete) {
    try {
      await api.apiDeleteAbsence(a.id);
    } catch (e) {
      console.warn('Errore eliminazione assenza:', a.id, e);
    }
  }
  const created = [];
  for (const c of toCreate) {
    try {
      const id = await api.apiCreateAbsence(c);
      created.push({ ...c, id });
    } catch (e) {
      console.warn('Errore creazione assenza unita:', e);
    }
  }
  if (toDelete.length > 0 || created.length > 0) {
    const idsToDel = new Set(toDelete.map(a => a.id));
    setState(s => ({
      ...s,
      absences: s.absences.filter(a => !idsToDel.has(a.id)).concat(created),
    }));
  }
}

export async function initStore() {
  try {
    const ok = await api.checkApiAvailable();
    if (!ok) {
      state = { ...getDefaultState(), _backendConnected: false };
      listeners.forEach(fn => fn(state));
      return false;
    }
    let data = await api.getFullState();

      const shiftSlots = (data.shiftSlots || []).filter(Boolean).map(normalizeApiSlot);
      const shiftAssignments = data.shiftAssignments || {};
      const lockedAssignments = data.lockedAssignments || {};
      const byKey = {};
      const lockedByKey = {};
      shiftSlots.forEach((s) => {
        if (!s?.id) return;
        const key =
          s.slotKey
          || stableCalendarSlotKey(s)
          || (s.date && s.timeSlot && s.category ? `${s.date}-${s.timeSlot}-${s.category}-${s.uocId || ''}-tpl` : null);
        if (!key) return;
        const docId = shiftAssignments[s.id];
        if (docId) byKey[key] = docId;
        if (lockedAssignments[s.id]) lockedByKey[key] = true;
      });
      const apiTpl = data.calendarTemplates || [];
      let finalTemplates = apiTpl;
      if (apiTpl.length === 0) {
        const defaults = getDefaultCalendarTemplates();
        for (const t of defaults) {
          try {
            await api.apiCreateTemplate({
              uocId: t.uocId,
              weekdays: t.weekdays,
              weekday: t.weekday,
              timeSlot: t.timeSlot,
              category: t.category,
              activityType: t.activityType || 'clinico',
              name: t.name,
              dateFrom: t.dateFrom || null,
              dateTo: t.dateTo || null,
            });
          } catch (e) {
            console.warn('Creazione template predefinito fallita:', t.name, e);
          }
        }
        data = await api.getFullState();
        finalTemplates = data.calendarTemplates || defaults;
      }
      state = {
        ...getDefaultState(),
        currentUser: api.getStoredUser(),
        doctors: (data.doctors || []).filter(Boolean).map(normalizeApiDoctor),
        absences: (data.absences || []).filter(Boolean).map(normalizeApiAbsence),
        desiderata: data.desiderata || [],
        shiftSlots,
        shiftAssignmentsByKey: byKey,
        lockedAssignmentsByKey: lockedByKey,
        calendarTemplates: (finalTemplates || []).map(normalizeCalendarTemplateRow),
        calendarModifications: data.calendarModifications || [],
        uocs: data.uocs || [],
        currentMonth: state.currentMonth,
        shiftPlanWeekFrom: state.shiftPlanWeekFrom,
        shiftPlanWeekTo: state.shiftPlanWeekTo,
        workloadBimester: state.workloadBimester,
        workloadAnnualYear: state.workloadAnnualYear,
        absencesCalendarWeekFrom: state.absencesCalendarWeekFrom,
        absencesCalendarWeekTo: state.absencesCalendarWeekTo,
        calendarWeekStart: state.calendarWeekStart,
        workloadHistory: data.workloadHistory ?? state.workloadHistory ?? {},
        workloadBimesterSnapshots: data.workloadBimesterSnapshots ?? state.workloadBimesterSnapshots ?? {},
        lockedWeeks: (data.lockedWeeks || []).reduce((acc, ws) => ({ ...acc, [ws]: true }), {}),
        absenceRequestLockedWeeks: (data.absenceRequestLockedWeeks || []).reduce((acc, ws) => ({ ...acc, [ws]: true }), {}),
        shiftImportIssues: Array.isArray(data.shiftImportIssues) ? data.shiftImportIssues : [],
        recurringExclusions: (data.recurringExclusions || []).map(r => ({
          id: r.id,
          doctorId: r.doctorId ?? r.doctor_id,
          category: r.category,
          weekday: r.weekday,
          slotTypes: r.slotTypes ?? (typeof r.slot_types === 'string' ? JSON.parse(r.slot_types || '[]') : r.slot_types || []),
          dateFrom: r.dateFrom ?? (r.date_from || null),
          dateTo: r.dateTo ?? (r.date_to || null),
        })),
        recurringOverrides: (data.recurringOverrides || []).map(o => ({
          recurringId: o.recurringId ?? o.recurring_id,
          date: o.date,
        })),
        _backendConnected: true,
      };
      // Primo paint subito: mergeDuplicateAbsences può richiedere molte richieste HTTP sequenziali
      // e lasciare l'utente su "Caricamento..." per troppo tempo (soprattutto in produzione).
      listeners.forEach(fn => fn(state));
      void mergeDuplicateAbsences().catch((e) => console.warn('mergeDuplicateAbsences:', e));
      return true;
  } catch (e) {
    console.warn('Backend non disponibile:', e);
    state = { ...getDefaultState(), _backendConnected: false };
    listeners.forEach(fn => fn(state));
    return false;
  }
}

// === Medici ===
const DOCTOR_WEEKLY_DEBITO_MIN = 0;
const DOCTOR_WEEKLY_DEBITO_MAX = 40;

/** Chiave bimestre "YYYY-Bn" valida, altrimenti null (il saldo di apertura resta inapplicato). */
function normalizeBimesterKey(raw) {
  const s = String(raw ?? '').trim();
  return /^\d{4}-B[1-6]$/.test(s) ? s : null;
}

function normalizeWeeklyDebitoHours(raw) {
  const n = Number(raw);
  if (Number.isFinite(n) && n >= DOCTOR_WEEKLY_DEBITO_MIN && n <= DOCTOR_WEEKLY_DEBITO_MAX) {
    return Math.round(n);
  }
  return null;
}

/** Payload API allineato a DoctorCreate (backend): evita campi legacy/snake_case nel body. */
function doctorToApiPayload(doctor) {
  const debito = normalizeWeeklyDebitoHours(
    doctor.debitoOrarioSettimanale ?? doctor.debito_orario_settimanale,
  );
  if (debito === null) {
    throw new Error('Debito orario settimanale non valido');
  }
  return {
    nome: String(doctor.nome || '').trim(),
    cognome: String(doctor.cognome || '').trim(),
    sigla: String(doctor.sigla || '').toUpperCase().slice(0, 4),
    debitoOrarioSettimanale: debito,
    esenzioneNotti: Boolean(doctor.esenzioneNotti),
    esenzioneWeekend: Boolean(doctor.esenzioneWeekend),
    esenzioneAttivitaCliniche: Boolean(doctor.esenzioneAttivitaCliniche),
    dataFineRapporto: doctor.dataFineRapporto || null,
    abilitaDirezione: Boolean(doctor.abilitaDirezione),
    specializzando: Boolean(doctor.specializzando),
    saldoOreApertura: Number(doctor.saldoOreApertura) || 0,
    saldoOreAperturaBimestre: normalizeBimesterKey(doctor.saldoOreAperturaBimestre),
    distribuzione: migrateDoctorDistribuzione(doctor.distribuzione),
  };
}

function doctorFromApiPayload(id, payload) {
  return { id, ...payload };
}

export async function addDoctor(doctor) {
  const payload = doctorToApiPayload(doctor);
  const id = await api.apiCreateDoctor(payload);
  const d = doctorFromApiPayload(id, payload);
  setState(s => ({ ...s, doctors: [...s.doctors, d] }));
  return id;
}

export async function updateDoctor(id, updates) {
  const full = state.doctors.find(d => d.id === id);
  if (!full) throw new Error('Medico non trovato');
  const payload = doctorToApiPayload({ ...full, ...updates });
  await api.apiUpdateDoctor(id, payload);
  const next = doctorFromApiPayload(id, payload);
  setState(s => ({
    ...s,
    doctors: s.doctors.map(d => (d.id === id ? next : d)),
  }));
}

export async function deleteDoctor(id) {
  await api.apiDeleteDoctor(id);
  setState(s => ({
    ...s,
    doctors: s.doctors.filter(d => d.id !== id),
    absences: s.absences.filter(a => a.doctorId !== id),
    desiderata: s.desiderata.filter(d => d.doctorId !== id),
  }));
}

export function getDoctor(id) {
  return state.doctors.find(d => d.id === id);
}

// Calcolo slot settimanali da percentuale
export function slotsFromPercentage(percent, weeklyHours = DEFAULT_WEEKLY_HOURS) {
  const totalSlots = weeklyHours / HOURS_PER_SLOT;
  return Math.round((percent / 100) * totalSlots * 10) / 10;
}

// === Assenze ===
export async function addAbsence(absence) {
  const dateStart = absence.dateStart?.slice(0, 10) || '';
  const dateEnd = absence.dateEnd?.slice(0, 10) || dateStart;
  const category = absence.category || ABSENCE_CATEGORIES.CONGEDO_ORDINARIO;
  const a = {
    id: generateId(),
    doctorId: absence.doctorId,
    dateStart,
    dateEnd,
    category,
    slotTypes: absence.slotTypes || [],
    oreFormazione: absence.oreFormazione ?? 0,
  };
  a.id = await api.apiCreateAbsence(a);
  setState(s => ({ ...s, absences: [...s.absences, a] }));
  await mergeDuplicateAbsences();
  return a.id;
}

export function updateAbsence(id, updates) {
  setState(s => ({
    ...s,
    absences: s.absences.map(a => (a.id === id ? { ...a, ...updates } : a)),
  }));
}

export async function deleteAbsence(id) {
  await api.apiDeleteAbsence(id);
  setState(s => ({ ...s, absences: s.absences.filter(a => a.id !== id) }));
}

export function getAbsencesForDoctor(doctorId) {
  return state.absences.filter(a => a.doctorId === doctorId);
}

/** Espande le esclusioni ricorrenti in assenze virtuali per l'intervallo date. Ritorna assenze con id "recurring:${recurringId}:${date}" */
function expandRecurringToAbsences(startDate, endDate) {
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  const overridesSet = new Set(state.recurringOverrides.map(o => `${o.recurringId}|${o.date}`));
  const virtual = [];
  for (const rec of state.recurringExclusions || []) {
    const from = rec.dateFrom ? new Date(rec.dateFrom + 'T12:00:00') : null;
    const to = rec.dateTo ? new Date(rec.dateTo + 'T12:00:00') : null;
    const cur = new Date(start);
    while (cur <= end) {
      const dateStr = cur.toISOString().slice(0, 10);
      const wd = cur.getDay();
      const weekday = wd === 0 ? 'dom' : WEEKDAYS[wd - 1];
      if (rec.weekday !== weekday) {
        cur.setDate(cur.getDate() + 1);
        continue;
      }
      if (from && cur < from) {
        cur.setDate(cur.getDate() + 1);
        continue;
      }
      if (to && cur > to) {
        cur.setDate(cur.getDate() + 1);
        continue;
      }
      if (overridesSet.has(`${rec.id}|${dateStr}`)) {
        cur.setDate(cur.getDate() + 1);
        continue;
      }
      virtual.push({
        id: `recurring:${rec.id}:${dateStr}`,
        _recurringId: rec.id,
        doctorId: rec.doctorId,
        dateStart: dateStr,
        dateEnd: dateStr,
        category: rec.category,
        slotTypes: rec.slotTypes || [],
        oreFormazione: 0,
      });
      cur.setDate(cur.getDate() + 1);
    }
  }
  return virtual;
}

/** Assenze effettive (reali + virtuali da ricorrenti) per l'intervallo. Usare per calendario e validazione turni. */
export function getEffectiveAbsences(startDate, endDate) {
  const real = state.absences.filter(a => {
    const start = a.dateStart?.slice(0, 10);
    const end = a.dateEnd?.slice(0, 10) || start;
    return start <= endDate && end >= startDate;
  });
  const virtual = expandRecurringToAbsences(startDate, endDate);
  return [...real, ...virtual];
}

export function getRecurringExclusionsForDoctor(doctorId) {
  return (state.recurringExclusions || []).filter(r => r.doctorId === doctorId);
}

export async function addRecurringExclusion(rec) {
  const id = await api.apiCreateRecurringExclusion(rec);
  setState(s => ({
    ...s,
    recurringExclusions: [...(s.recurringExclusions || []), { ...rec, id }],
  }));
  return id;
}

export async function deleteRecurringExclusion(id) {
  await api.apiDeleteRecurringExclusion(id);
  setState(s => ({
    ...s,
    recurringExclusions: (s.recurringExclusions || []).filter(r => r.id !== id),
    recurringOverrides: (s.recurringOverrides || []).filter(o => o.recurringId !== id),
  }));
}

/** Aggiunge override per escludere una singola data da una regola ricorrente (quando si cancella dal calendario) */
export async function addRecurringOverride(recurringId, date) {
  await api.apiAddRecurringOverride(recurringId, date);
  setState(s => ({
    ...s,
    recurringOverrides: [...(s.recurringOverrides || []), { recurringId, date }],
  }));
}

/** Medico ha almeno un giorno feriale (lun-ven) nella settimana in cui non è assente. Usato per escludere dai pool chi è in congedo full-week. */
export function hasAvailableDayInWeek(doctorId, weekStart, weekEndStr) {
  const start = new Date(weekStart + 'T12:00:00');
  const end = new Date(weekEndStr + 'T12:00:00');
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const wd = d.getDay();
    if (wd === 0 || wd === 6) continue;
    const dateStr = d.toISOString().slice(0, 10);
    if (!isDoctorAbsent(doctorId, dateStr)) return true;
  }
  return false;
}

/** Verifica se il medico è assente in data (da e a compresi). Libera Professione e Formazione sono slot-specifici, non bloccano l'intera giornata. */
export function isDoctorAbsent(doctorId, date) {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  const absences = getEffectiveAbsences(dateStr, dateStr);
  return absences.some(a => {
    if (a.doctorId !== doctorId || !a.dateStart || !a.dateEnd) return false;
    if (a.category === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE || a.category === ABSENCE_CATEGORIES.FORMAZIONE) return false;
    const start = a.dateStart.slice(0, 10);
    const end = a.dateEnd.slice(0, 10);
    return dateStr >= start && dateStr <= end;
  });
}

/** Blocco slot per Libera Professione o Formazione (esclusione selettiva mattino/pomeriggio/notte) */
export function hasSlotBlockForAbsence(doctorId, date, timeSlot) {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  const slotMap = { mattino: 'mattino', pomeriggio: 'pomeriggio', prolungato: 'prolungato', notte: 'notte', reperibilita_notte: 'notte', reperibilita_giorno: 'pomeriggio' };
  const slot = slotMap[timeSlot] || timeSlot;
  const absences = getEffectiveAbsences(dateStr, dateStr);
  return absences.some(a => {
    if (a.doctorId !== doctorId || !a.dateStart || !a.dateEnd) return false;
    if (a.category !== ABSENCE_CATEGORIES.LIBERA_PROFESSIONE && a.category !== ABSENCE_CATEGORIES.FORMAZIONE) return false;
    const start = a.dateStart.slice(0, 10);
    const end = a.dateEnd.slice(0, 10);
    if (dateStr < start || dateStr > end) return false;
    const types = a.slotTypes || [];
    if (types.length === 0) return false;
    return types.includes(slot);
  });
}

/** Restituisce { liberaProfessione: string[], formazione: string[] } per medico/date (slot esclusi) */
export function getSlotBlocksForDoctorDate(doctorId, date) {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  const result = { liberaProfessione: [], formazione: [] };
  const absences = getEffectiveAbsences(dateStr, dateStr);
  for (const a of absences) {
    if (a.doctorId !== doctorId || !a.dateStart || !a.dateEnd) continue;
    const start = a.dateStart.slice(0, 10);
    const end = a.dateEnd.slice(0, 10);
    if (dateStr < start || dateStr > end) continue;
    const types = a.slotTypes || [];
    if (a.category === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE) result.liberaProfessione.push(...types);
    if (a.category === ABSENCE_CATEGORIES.FORMAZIONE) result.formazione.push(...types);
  }
  result.liberaProfessione = [...new Set(result.liberaProfessione)];
  result.formazione = [...new Set(result.formazione)];
  return result;
}

/** Formazione con ore ≥ questo valore = «giornata intera» su un solo giorno (7,6h quel giorno). Su più giorni vale sempre 7,6h per ogni giorno dell’intervallo. */
const FORMAZIONE_FULL_DAY_ORE_THRESHOLD = HOURS_PER_DAY;

/** Ore formazione creditate per un medico in una settimana. Intervallo su più giorni: 7,6h × giorni nell’overlap; un solo giorno con ore ≥ 7,6: 7,6h; un solo giorno parziale: oreFormazione. */
export function getFormazioneOreForWeek(doctorId, weekStart) {
  const endStr = addCalendarDays(weekStart, 6);
  let total = 0;
  for (const a of state.absences) {
    if (a.doctorId !== doctorId || a.category !== ABSENCE_CATEGORIES.FORMAZIONE || (a.oreFormazione || 0) <= 0) continue;
    const start = a.dateStart.slice(0, 10);
    const end = (a.dateEnd || a.dateStart).slice(0, 10);
    if (start > endStr || end < weekStart) continue;
    const overlapStart = start < weekStart ? weekStart : start;
    const overlapEnd = end > endStr ? endStr : end;
    const ore = a.oreFormazione || 0;
    let ndays = 0;
    for (let d = new Date(overlapStart + 'T12:00:00'); d <= new Date(overlapEnd + 'T12:00:00'); d.setDate(d.getDate() + 1)) ndays++;
    if (ndays > 1) {
      total += ndays * HOURS_PER_DAY;
    } else if (ore >= FORMAZIONE_FULL_DAY_ORE_THRESHOLD) {
      total += HOURS_PER_DAY;
    } else {
      total += ore;
    }
  }
  return total;
}

/** Ore creditate per assenze a giornata intera (Congedo Ordinario, Permesso, Rischio Radiologico, Congedo Malattia/Maternità): sempre 7,6h per giorno feriale non festivo (riferimento 38h/sett). */
const CREDITED_ABSENCE_CATEGORIES = [
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE,
  ABSENCE_CATEGORIES.PERMESSO,
  ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO,
  ABSENCE_CATEGORIES.CONGEDO_MALATTIA,
];

/** Ore per giorno feriale da debito orario settimanale (÷5). Usato da solver, Carichi, vincoli.
 *  0 è un valore valido ("nessun debito clinico") e restituisce 0 ore/giorno: il fallback
 *  a tempo pieno vale solo se il campo manca o non è numerico. */
export function getContractDailyHoursForDoctor(doc) {
  if (!doc) return HOURS_PER_DAY;
  const debito = Number(doc.debitoOrarioSettimanale);
  if (Number.isFinite(debito) && debito >= 0) return debito / 5;
  return HOURS_PER_DAY;
}

export function getHoursPerDayForDoctorOnDate(doctorId, dateStr) {
  void dateStr;
  const doc = state.doctors.find(d => d.id === doctorId);
  return getContractDailyHoursForDoctor(doc);
}

/** Aggiunge un giorno a una data YYYY-MM-DD (evita problemi timezone di toISOString). */
function addOneDay(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Ore creditate per giorno. Congedo/Permesso/Rischio/Malattia: lun–ven non festivi, 7,6h/giorno. Formazione su più giorni: 7,6h per ogni giorno del periodo (sab, dom e festivi compresi). Formazione un solo giorno: intera se oreFormazione ≥ 7,6 (anche festivo); altrimenti lun–ven non festivo × oreFormazione. Congedo e Formazione stesso giorno: max. */
function getCreditedHoursPerDay(doctorId, startDate, endDate) {
  const absences = getEffectiveAbsences(startDate, endDate);
  const congedoByDay = {};   // dateStr -> ore (lun–ven non festivi, 7.6h)
  const formazioneByDay = {}; // dateStr -> ore
  const credCats = new Set(CREDITED_ABSENCE_CATEGORIES);

  const festiviDates = new Set();
  const y1 = parseInt(startDate.slice(0, 4), 10);
  const y2 = parseInt(endDate.slice(0, 4), 10);
  for (let y = y1; y <= y2; y++) {
    for (const d of getFestiviInfrasettimanali(y)) festiviDates.add(d);
  }

  for (const a of absences) {
    if (a.doctorId !== doctorId) continue;
    const start = a.dateStart?.slice(0, 10);
    const end = (a.dateEnd || a.dateStart)?.slice(0, 10);
    if (!start || !end) continue;
    const overlapStart = start < startDate ? startDate : start;
    const overlapEnd = end > endDate ? endDate : end;
    if (overlapStart > overlapEnd) continue;
    const cat = (a.category || '').trim();
    const isCongedoPermessoRischioMalattia = credCats.has(cat);
    const isFormazione = cat === ABSENCE_CATEGORIES.FORMAZIONE;
    const oreF = a.oreFormazione || 0;
    const isFullDayFormazione = isFormazione && oreF >= FORMAZIONE_FULL_DAY_ORE_THRESHOLD;
    let ndaysInOverlap = 0;
    for (let t = overlapStart; t <= overlapEnd; t = addOneDay(t)) ndaysInOverlap++;
    const multiDayFormazione = isFormazione && oreF > 0 && ndaysInOverlap > 1;

    for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) {
      const wd = new Date(ds + 'T12:00:00').getDay();
      if (isCongedoPermessoRischioMalattia) {
        if (wd === 0 || wd === 6) continue;
        if (festiviDates.has(ds)) continue;
        congedoByDay[ds] = HOURS_PER_DAY;
      } else if (isFormazione && oreF > 0) {
        if (multiDayFormazione) {
          formazioneByDay[ds] = Math.max(formazioneByDay[ds] || 0, HOURS_PER_DAY);
        } else if (isFullDayFormazione) {
          formazioneByDay[ds] = HOURS_PER_DAY;
        } else {
          if (wd === 0 || wd === 6) continue;
          if (festiviDates.has(ds)) continue;
          formazioneByDay[ds] = (formazioneByDay[ds] || 0) + oreF;
        }
      }
    }
  }
  const byDay = {};
  const allDays = new Set([...Object.keys(congedoByDay), ...Object.keys(formazioneByDay)]);
  for (const ds of allDays) {
    const c = congedoByDay[ds] || 0;
    const f = formazioneByDay[ds] || 0;
    byDay[ds] = Math.max(c, f);
  }
  return byDay;
}

export function getCreditedAbsenceHoursForRange(doctorId, startDate, endDate) {
  const byDay = getCreditedHoursPerDay(doctorId, startDate, endDate);
  return Object.values(byDay).reduce((s, h) => s + h, 0);
}

/** Ore perse per disponibilità: allineata al monte accreditato (congedo ecc. lun–ven non festivo; formazione multi-giorno o intera: 7,6h/giorno calendario; formazione parziale un giorno: min(ore, hoursPerDay) su lun–ven non festivo). */
export function getHoursLostForAvailability(doctorId, startDate, endDate, hoursPerDay) {
  const absences = getEffectiveAbsences(startDate, endDate);
  const credCats = new Set(CREDITED_ABSENCE_CATEGORIES);
  const festiviDates = new Set();
  const y1 = parseInt(startDate.slice(0, 4), 10);
  const y2 = parseInt(endDate.slice(0, 4), 10);
  for (let y = y1; y <= y2; y++) {
    for (const d of getFestiviInfrasettimanali(y)) festiviDates.add(d);
  }
  let total = 0;
  for (const a of absences) {
    if (a.doctorId !== doctorId) continue;
    const start = a.dateStart?.slice(0, 10);
    const end = (a.dateEnd || a.dateStart)?.slice(0, 10);
    if (!start || !end) continue;
    const overlapStart = start < startDate ? startDate : start;
    const overlapEnd = end > endDate ? endDate : end;
    if (overlapStart > overlapEnd) continue;
    const cat = (a.category || '').trim();
    const isCredited = credCats.has(cat);
    const isFormazione = cat === ABSENCE_CATEGORIES.FORMAZIONE;
    const oreF = a.oreFormazione || 0;
    const isFullDayFormazione = isFormazione && oreF >= FORMAZIONE_FULL_DAY_ORE_THRESHOLD;
    let ndLost = 0;
    for (let t = overlapStart; t <= overlapEnd; t = addOneDay(t)) ndLost++;
    const multiDayFormazione = isFormazione && oreF > 0 && ndLost > 1;
    if (isCredited) {
      for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) {
        const wd = new Date(ds + 'T12:00:00').getDay();
        if (wd === 0 || wd === 6) continue;
        if (festiviDates.has(ds)) continue;
        total += HOURS_PER_DAY;
      }
    } else if (isFormazione && oreF > 0) {
      for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) {
        if (multiDayFormazione || isFullDayFormazione) {
          total += HOURS_PER_DAY;
        } else {
          const wd = new Date(ds + 'T12:00:00').getDay();
          if (wd === 0 || wd === 6) continue;
          if (festiviDates.has(ds)) continue;
          total += Math.min(oreF, hoursPerDay);
        }
      }
    }
  }
  return total;
}

/** Date in cui il medico va ESCLUSO dai turni (per solver). Congedo/Permesso/Rischio/Malattia: tutte le date del periodo (anche weekend). Formazione su più giorni o «intera»: ogni giorno dell’intervallo. Formazione parziale un giorno: solo se oreFormazione ≥ ore/giorno medico (lun–ven, non festivi). */
export function getAbsenceDatesForExclusion(doctorId, startDate, endDate) {
  const absences = getEffectiveAbsences(startDate, endDate);
  const credCats = new Set(CREDITED_ABSENCE_CATEGORIES);
  const festiviDates = new Set();
  const y1 = parseInt(startDate.slice(0, 4), 10);
  const y2 = parseInt(endDate.slice(0, 4), 10);
  for (let y = y1; y <= y2; y++) {
    for (const d of getFestiviInfrasettimanali(y)) festiviDates.add(d);
  }
  const dates = new Set();
  for (const a of absences) {
    if (a.doctorId !== doctorId) continue;
    const start = a.dateStart?.slice(0, 10);
    const end = (a.dateEnd || a.dateStart)?.slice(0, 10);
    if (!start || !end) continue;
    const overlapStart = start < startDate ? startDate : start;
    const overlapEnd = end > endDate ? endDate : end;
    if (overlapStart > overlapEnd) continue;
    const cat = (a.category || '').trim();
    const isCongedoPermessoRischioMalattia = credCats.has(cat);
    const isFormazione = cat === ABSENCE_CATEGORIES.FORMAZIONE;
    const oreF = a.oreFormazione || 0;
    const isFullDayFormazione = isFormazione && oreF >= FORMAZIONE_FULL_DAY_ORE_THRESHOLD;
    let ndEx = 0;
    for (let t = overlapStart; t <= overlapEnd; t = addOneDay(t)) ndEx++;
    const multiDayFormazione = isFormazione && oreF > 0 && ndEx > 1;
    if (isCongedoPermessoRischioMalattia) {
      for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) dates.add(ds);
    } else if (isFormazione && oreF > 0) {
      for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) {
        if (multiDayFormazione || isFullDayFormazione) {
          dates.add(ds);
        } else {
          const wd = new Date(ds + 'T12:00:00').getDay();
          if (wd === 0 || wd === 6) continue;
          if (festiviDates.has(ds)) continue;
          const hpd = getHoursPerDayForDoctorOnDate(doctorId, ds);
          if (oreF >= hpd) dates.add(ds);
        }
      }
    }
  }
  return dates;
}

/** Giorni "coperti" da assenze a giornata intera (valgono come giorno lavorato per max 5 giorni/settimana).
 * Congedo/Permesso/Rischio/Malattia: lun–ven, esclusi festivi infrasettimanali.
 * Formazione su più giorni o intera (oreFormazione ≥ 7,6): ogni giorno del periodo (sab, dom e festivi compresi). Formazione parziale un solo giorno: solo se oreFormazione ≥ ore/giorno medico su lun–ven non festivi. */
export function getFullDayAbsenceDates(doctorId, startDate, endDate) {
  const absences = getEffectiveAbsences(startDate, endDate);
  const credCats = new Set(CREDITED_ABSENCE_CATEGORIES);
  const festiviDates = new Set();
  const y1 = parseInt(startDate.slice(0, 4), 10);
  const y2 = parseInt(endDate.slice(0, 4), 10);
  for (let y = y1; y <= y2; y++) {
    for (const d of getFestiviInfrasettimanali(y)) festiviDates.add(d);
  }
  const dates = new Set();
  for (const a of absences) {
    if (a.doctorId !== doctorId) continue;
    const start = a.dateStart?.slice(0, 10);
    const end = (a.dateEnd || a.dateStart)?.slice(0, 10);
    if (!start || !end) continue;
    const overlapStart = start < startDate ? startDate : start;
    const overlapEnd = end > endDate ? endDate : end;
    if (overlapStart > overlapEnd) continue;
    const cat = (a.category || '').trim();
    const isCongedoPermessoRischioMalattia = credCats.has(cat);
    const isFormazione = cat === ABSENCE_CATEGORIES.FORMAZIONE;
    const oreF = a.oreFormazione || 0;
    const isFullDayFormazione = isFormazione && oreF >= FORMAZIONE_FULL_DAY_ORE_THRESHOLD;
    let ndFd = 0;
    for (let t = overlapStart; t <= overlapEnd; t = addOneDay(t)) ndFd++;
    const multiDayFormazione = isFormazione && oreF > 0 && ndFd > 1;
    for (let ds = overlapStart; ds <= overlapEnd; ds = addOneDay(ds)) {
      const wd = new Date(ds + 'T12:00:00').getDay();
      if (isCongedoPermessoRischioMalattia) {
        if (wd === 0 || wd === 6) continue;
        if (festiviDates.has(ds)) continue;
        dates.add(ds);
      } else if (isFormazione && oreF > 0) {
        if (multiDayFormazione || isFullDayFormazione) {
          dates.add(ds);
        } else {
          if (wd === 0 || wd === 6) continue;
          if (festiviDates.has(ds)) continue;
          const hpd = getHoursPerDayForDoctorOnDate(doctorId, ds);
          if (oreF >= hpd) dates.add(ds);
        }
      }
    }
  }
  return dates;
}

// === Desiderata ===
export async function setDesiderata(doctorId, date, types) {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  await api.apiSetDesiderata(doctorId, dateStr, types);
  setState(s => {
    const filtered = s.desiderata.filter(
      d => !(d.doctorId === doctorId && d.date === dateStr)
    );
    if (types.length > 0) {
      filtered.push({ doctorId, date: dateStr, types });
    }
    return { ...s, desiderata: filtered };
  });
}

export function getDesiderataForDoctorDate(doctorId, date) {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  const d = state.desiderata.find(
    x => x.doctorId === doctorId && x.date === dateStr
  );
  return d ? d.types : [];
}

export function hasDesiderataBlock(doctorId, date, timeSlot) {
  const types = getDesiderataForDoctorDate(doctorId, date);
  if (types.includes(DESIDERATA_TYPES.NO_MATTINO) && (timeSlot === 'mattino' || timeSlot === 'prolungato' || timeSlot === 'reperibilita_giorno')) return true;
  if (types.includes(DESIDERATA_TYPES.NO_POMERIGGIO) && (timeSlot === 'pomeriggio' || timeSlot === 'reperibilita_giorno')) return true;
  if (types.includes(DESIDERATA_TYPES.NO_NOTTE) && (timeSlot === 'notte' || timeSlot === 'reperibilita_notte')) return true;
  if (types.includes(DESIDERATA_TYPES.NO_POMERIGGIO_NOTTE) && (timeSlot === 'pomeriggio' || timeSlot === 'notte' || timeSlot === 'reperibilita_notte' || timeSlot === 'reperibilita_giorno')) return true;
  if (types.includes(DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO) && timeSlot === 'reperibilita_giorno') return true;
  if (types.includes(DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE) && timeSlot === 'reperibilita_notte') return true;
  // Legacy: token "LP" / "libera_professione" in desiderata → blocco diurno (allineato al backend).
  const hasLegacyLp = types.some(t => {
    const compact = String(t || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    return compact === 'lp' || compact.startsWith('lp') || compact.includes('liberaprofessione');
  });
  if (hasLegacyLp && (timeSlot === 'mattino' || timeSlot === 'pomeriggio' || timeSlot === 'prolungato' || timeSlot === 'reperibilita_giorno')) return true;
  return false;
}

/** Tipi di desiderata che bloccano una specifica fascia oraria (inverso di hasDesiderataBlock). */
export function desiderataTypesBlockingSlot(timeSlot) {
  const t = DESIDERATA_TYPES;
  const blocking = [];
  if (timeSlot === 'mattino' || timeSlot === 'prolungato' || timeSlot === 'reperibilita_giorno') blocking.push(t.NO_MATTINO);
  if (timeSlot === 'pomeriggio' || timeSlot === 'reperibilita_giorno') blocking.push(t.NO_POMERIGGIO);
  if (timeSlot === 'notte' || timeSlot === 'reperibilita_notte') blocking.push(t.NO_NOTTE);
  if (timeSlot === 'pomeriggio' || timeSlot === 'notte' || timeSlot === 'reperibilita_notte' || timeSlot === 'reperibilita_giorno') blocking.push(t.NO_POMERIGGIO_NOTTE);
  if (timeSlot === 'reperibilita_giorno') blocking.push(t.NO_REPERIBILITA_GIORNO);
  if (timeSlot === 'reperibilita_notte') blocking.push(t.NO_REPERIBILITA_NOTTE);
  return [...new Set(blocking)];
}

/** Rimuove un singolo giorno (dateStr) da un record assenza reale, spezzando l'intervallo se necessario. */
async function removeDayFromAbsenceRecord(a, dateStr) {
  const start = (a.dateStart || '').slice(0, 10);
  const end = (a.dateEnd || '').slice(0, 10) || start;
  await deleteAbsence(a.id);
  if (start && start < dateStr) {
    await addAbsence({
      doctorId: a.doctorId,
      dateStart: start,
      dateEnd: addCalendarDays(dateStr, -1),
      category: a.category,
      slotTypes: a.slotTypes || [],
      oreFormazione: a.oreFormazione ?? 0,
    });
  }
  if (end && end > dateStr) {
    await addAbsence({
      doctorId: a.doctorId,
      dateStart: addCalendarDays(dateStr, 1),
      dateEnd: end,
      category: a.category,
      slotTypes: a.slotTypes || [],
      oreFormazione: a.oreFormazione ?? 0,
    });
  }
}

/**
 * Rimuove dal piano assenze le incompatibilità (assenza giustificata / desiderata) che bloccano
 * l'assegnazione manuale del medico allo slot M/P/Pr/N. Da usare SOLO dietro conferma esplicita.
 * - Desiderata: rimuove i soli tipi che bloccano la fascia del giorno dello slot.
 * - Assenze reali: rimuove il giorno dall'intervallo (spezzandolo se necessario).
 * - Esclusioni ricorrenti: aggiunge un override per quel singolo giorno.
 * Per le notti cliniche libera anche il giorno di uscita (D+1) se ha un'assenza intera giornata.
 * Ritorna { removedDesiderata, removedAbsenceDays }.
 */
export async function clearAbsenceDesiderataConflictForSlot(doctorId, slot) {
  const result = { removedDesiderata: false, removedAbsenceDays: 0 };
  if (!slot || !slot.date) return result;
  const slotDate = slot.date.slice(0, 10);
  const timeSlot = slot.timeSlot;
  const slotMap = { mattino: 'mattino', pomeriggio: 'pomeriggio', prolungato: 'prolungato', notte: 'notte', reperibilita_notte: 'notte', reperibilita_giorno: 'pomeriggio' };

  // 1. Desiderata in conflitto (giorno dello slot)
  const currentTypes = getDesiderataForDoctorDate(doctorId, slotDate);
  if (currentTypes.length) {
    const blocking = new Set(desiderataTypesBlockingSlot(timeSlot));
    const remaining = currentTypes.filter(t => !blocking.has(t));
    if (remaining.length !== currentTypes.length) {
      await setDesiderata(doctorId, slotDate, remaining);
      result.removedDesiderata = true;
    }
  }

  // 2. Giorni di assenza da liberare
  const isClinicalNight = timeSlot === 'notte' && slot.activityType !== 'reperibilita';
  const datesToClear = [{ date: slotDate, perSlot: true }];
  if (isClinicalNight) {
    const egress = addCalendarDays(slotDate, 1);
    if (getFullDayAbsenceDates(doctorId, egress, egress).size > 0) {
      datesToClear.push({ date: egress, perSlot: false });
    }
  }

  const blocksDate = (a, mappedSlot) => {
    const isLP = a.category === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE || a.category === ABSENCE_CATEGORIES.FORMAZIONE;
    if (mappedSlot != null) {
      if (isLP) return (a.slotTypes || []).includes(mappedSlot);
      return true; // assenza intera giornata blocca tutte le fasce
    }
    // giorno di uscita notte: conta solo l'assenza intera giornata (no Libera Professione)
    return a.category !== ABSENCE_CATEGORIES.LIBERA_PROFESSIONE;
  };

  for (const { date: dateStr, perSlot } of datesToClear) {
    const mappedSlot = perSlot ? (slotMap[timeSlot] || timeSlot) : null;

    const realAffected = state.absences.filter(a => {
      if (a.doctorId !== doctorId) return false;
      if (typeof a.id === 'string' && a.id.startsWith('recurring:')) return false;
      const start = (a.dateStart || '').slice(0, 10);
      const end = (a.dateEnd || '').slice(0, 10) || start;
      if (!start || dateStr < start || dateStr > end) return false;
      return blocksDate(a, mappedSlot);
    });
    for (const a of realAffected) {
      await removeDayFromAbsenceRecord(a, dateStr);
      result.removedAbsenceDays++;
    }

    const virtual = expandRecurringToAbsences(dateStr, dateStr).filter(a => {
      if (a.doctorId !== doctorId) return false;
      return blocksDate(a, mappedSlot);
    });
    for (const a of virtual) {
      if (a._recurringId != null) {
        await addRecurringOverride(a._recurringId, dateStr);
        result.removedAbsenceDays++;
      }
    }
  }

  return result;
}

// === Slot turni ===
export function setCurrentMonth(month) {
  setState(s => ({ ...s, currentMonth: month }));
}

export function setShiftPlanWeekRange(weekFrom, weekTo) {
  setState(s => ({ ...s, shiftPlanWeekFrom: weekFrom, shiftPlanWeekTo: weekTo }));
}

export function setWorkloadBimester(bimesterKey) {
  setState(s => ({ ...s, workloadBimester: bimesterKey }));
}

export function setWorkloadAnnualYear(year) {
  setState(s => ({ ...s, workloadAnnualYear: year }));
}

export function setAbsencesCalendarWeekRange(weekFrom, weekTo) {
  setState(s => ({ ...s, absencesCalendarWeekFrom: weekFrom, absencesCalendarWeekTo: weekTo }));
}

export function setCalendarWeekStart(dateStr) {
  setState(s => ({ ...s, calendarWeekStart: dateStr }));
}

export async function addShiftSlot(slot) {
  const at = slot.activityType || 'clinico';
  const nm = ensurePlannerSlotName(slot.category, slot.name, slot.timeSlot, at);
  const s = {
    id: generateId(),
    name: nm,
    category: slot.category,
    activityType: at,
    timeSlot: slot.timeSlot,
    date: slot.date,
    weekday: slot.weekday,
    uocId: slot.uocId,
  };
  s.id = await api.apiCreateShiftSlot(s);
  setState(st => ({ ...st, shiftSlots: [...st.shiftSlots, s] }));
  return s.id;
}

export function updateShiftSlot(id, updates) {
  setState(s => ({
    ...s,
    shiftSlots: s.shiftSlots.map(sl => (sl.id === id ? { ...sl, ...updates } : sl)),
  }));
}

export async function deleteShiftSlot(id) {
  await api.apiDeleteShiftSlot(id);
  setState(s => {
    const { [id]: _, ...rest } = s.shiftAssignments || {};
    return {
      ...s,
      shiftSlots: s.shiftSlots.filter(sl => sl.id !== id),
      shiftAssignments: rest,
    };
  });
}

export async function assignDoctorToSlot(slotId, doctorId) {
  await api.apiSetAssignment(slotId, doctorId);
  setState(s => ({
    ...s,
    shiftAssignments: { ...s.shiftAssignments, [slotId]: doctorId },
  }));
}

export async function unassignSlot(slotId) {
  await api.apiDeleteAssignment(slotId);
  setState(s => {
    const next = { ...s.shiftAssignments };
    delete next[slotId];
    return { ...s, shiftAssignments: next };
  });
}

// === Calendar templates ===
export async function addCalendarTemplate(template) {
  const ws = sortWeekdaysCalendarOrder(template.weekdays || []);
  if (!ws.length) {
    throw new Error('Seleziona almeno un giorno della settimana');
  }
  const at = template.activityType || 'clinico';
  const name = ensurePlannerSlotName(template.category, template.name, template.timeSlot, at);
  const payload = {
    uocId: template.uocId,
    weekdays: ws,
    weekday: ws[0],
    timeSlot: template.timeSlot,
    category: template.category,
    activityType: at,
    name,
    dateFrom: template.dateFrom || null,
    dateTo: template.dateTo || null,
  };
  const id = await api.apiCreateTemplate(payload);
  const t = normalizeCalendarTemplateRow({ ...payload, id });
  setState(s => ({ ...s, calendarTemplates: [...s.calendarTemplates, t] }));
  return id;
}

export async function updateCalendarTemplate(id, template) {
  const cur = getState().calendarTemplates.find(t => t.id === id);
  if (!cur) throw new Error('Template non trovato');
  const merged = normalizeCalendarTemplateRow({ ...cur, ...template, id });
  const at = merged.activityType || 'clinico';
  const name = ensurePlannerSlotName(merged.category, merged.name, merged.timeSlot, at);
  const mergedOut = { ...merged, name, activityType: at };
  await api.apiUpdateTemplate(id, mergedOut);
  setState(s => ({
    ...s,
    calendarTemplates: s.calendarTemplates.map(t => (t.id === id ? mergedOut : t)),
  }));
}

export async function deleteCalendarTemplate(id) {
  await api.apiDeleteTemplate(id);
  setState(s => ({
    ...s,
    calendarTemplates: s.calendarTemplates.filter(t => t.id !== id),
  }));
}

// === Calendar modifications ===
export async function addCalendarModification(mod, opts = {}) {
  const at = mod.activityType || 'clinico';
  const cat = String(mod.category || '').trim();
  const isRemove = String(mod.modificationType || '') === 'remove';
  const rawName = mod.name == null ? '' : String(mod.name).trim();
  // Remove: nome vuoto = wildcard (non forzare SO1/MedDol via ensurePlannerSlotName).
  // Add: nome sempre canonico non vuoto.
  const name = isRemove && !rawName
    ? ''
    : ensurePlannerSlotName(cat, rawName, mod.timeSlot, at);
  const uocId = String(mod.uocId || CATEGORY_TO_UOC[cat] || '').trim();
  const m = {
    id: generateId(),
    modificationType: mod.modificationType,
    scope: mod.scope,
    uocId,
    weekday: mod.weekday,
    dateSpecific: mod.dateSpecific,
    dateFrom: mod.dateFrom,
    dateTo: mod.dateTo,
    timeSlot: mod.timeSlot,
    category: mod.category,
    activityType: at,
    name,
  };
  m.id = await api.apiCreateModification(m);
  setState(s => ({ ...s, calendarModifications: [...s.calendarModifications, m] }));
  if (!opts.skipRefresh) await refreshShiftAssignmentsFromApi();
  return m.id;
}

export async function updateCalendarModification(id, mod) {
  const cur = getState().calendarModifications.find(m => m.id === id);
  if (!cur) throw new Error('Modifica non trovata');
  const merged = { ...cur, ...mod, id };
  await api.apiUpdateModification(id, merged);
  const at = merged.activityType || 'clinico';
  const cat = String(merged.category || '').trim();
  const isRemove = String(merged.modificationType || '') === 'remove';
  const rawName = merged.name == null ? '' : String(merged.name).trim();
  const name = isRemove && !rawName
    ? ''
    : ensurePlannerSlotName(cat, rawName, merged.timeSlot, at);
  const uocId = String(merged.uocId || CATEGORY_TO_UOC[cat] || '').trim();
  setState(s => ({
    ...s,
    calendarModifications: s.calendarModifications.map(m =>
      m.id === id ? { ...merged, name, uocId, activityType: at, id: m.id } : m
    ),
  }));
}

export async function deleteCalendarModification(id, opts = {}) {
  await api.apiDeleteModification(id);
  setState(s => ({
    ...s,
    calendarModifications: s.calendarModifications.filter(m => m.id !== id),
  }));
  if (!opts.skipRefresh) await refreshShiftAssignmentsFromApi();
}

export function getAssignment(slotId) {
  return state.shiftAssignments?.[slotId];
}

export function getShiftImportIssues() {
  return Array.isArray(state.shiftImportIssues) ? [...state.shiftImportIssues] : [];
}

export function setShiftImportIssues(issues) {
  setState(s => ({ ...s, shiftImportIssues: Array.isArray(issues) ? issues : [] }));
}

function clearShiftImportIssuesForSlot(issues, slotKey, slot = null) {
  if (!Array.isArray(issues) || issues.length === 0) return [];
  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const key = slotKey == null ? '' : String(slotKey).trim();
  const plannerKey = norm(slot?.plannerCellKey || slot?.planner_cell_key || '');
  const d = norm(slot?.date);
  const ts = norm(slot?.timeSlot);
  const cat = norm(slot?.category);
  const name = norm(slot?.name);

  return issues.filter((x) => {
    if (!x) return false;
    // Match primario: stesso slot_key del warning.
    if (key && x.slot_key && String(x.slot_key) === key) return false;
    if (plannerKey && norm(x.planner_cell_key) === plannerKey) return false;
    // Match secondario: warning senza slot_key (o non allineato), stessa cella logica.
    if (!d || !ts || !cat) return true;
    if (norm(x.date) !== d) return true;
    if (norm(x.time_slot) !== ts) return true;
    if (norm(x.category) !== cat) return true;
    const xn = norm(x.slot_name);
    if (name && xn) return xn !== name;
    return false;
  });
}

export function getShiftImportIssueBySlotOrDate(slotKey, date, timeSlot, category, slotName, plannerCellKey = '') {
  const all = state.shiftImportIssues || [];
  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const key = slotKey == null ? '' : String(slotKey).trim();
  const pKey = norm(plannerCellKey);
  if (pKey) {
    const byPlanner = all.find((x) => x && x.highlight && norm(x.planner_cell_key) === pKey);
    if (byPlanner) return byPlanner;
  }
  if (key) {
    const exact = all.find((x) => x && x.highlight && x.slot_key && String(x.slot_key) === key);
    if (exact) return exact;
  }
  const d = norm(date);
  const ts = norm(timeSlot);
  const cat = norm(category);
  const name = norm(slotName);
  if (!d || !ts || !cat) return null;
  // Match preciso a livello di cella: stessa data + fascia + categoria + (se disponibile) nome slot.
  // Niente match "broad" che illuminerebbe l'intera categoria/fascia.
  return (
    all.find((x) => {
      if (!x || !x.highlight) return false;
      if (norm(x.date) !== d) return false;
      if (norm(x.time_slot) !== ts) return false;
      if (norm(x.category) !== cat) return false;
      const xn = norm(x.slot_name);
      if (name && xn) return xn === name;
      return true;
    }) || null
  );
}

export function getAssignmentByKey(slotKey) {
  return state.shiftAssignmentsByKey?.[slotKey];
}

/** Ricarica le assegnazioni turni dal DB (per sincronizzare quando si apre il Piano Turni).
 *  Opzionalmente riallinea anche le modifiche calendario (es. one-off creati dall'import).
 */
export async function refreshShiftAssignmentsFromApi(opts = {}) {
  try {
    const refreshMods = Boolean(opts.refreshCalendarModifications);
    const [slots, assignments, modifications] = await Promise.all([
      api.apiGetShiftSlots(),
      api.apiGetAssignments(),
      refreshMods ? api.apiGetModifications() : Promise.resolve(null),
    ]);
    const normalizedSlots = (slots || []).filter(Boolean).map(normalizeApiSlot);
    const shiftAssignmentsFlat = {};
    const byKey = {};
    const lockedByKey = {};
    for (const [slotId, a] of Object.entries(assignments || {})) {
      if (!a || typeof a !== 'object') continue;
      if (a.doctorId) shiftAssignmentsFlat[slotId] = a.doctorId;
    }
    for (const s of normalizedSlots) {
      const key =
        s.slotKey
        ?? s.slot_key
        ?? stableCalendarSlotKey(s)
        ?? (s.date && s.timeSlot && s.category ? `${s.date}-${s.timeSlot}-${s.category}-${s.uocId || ''}-tpl` : null);
      const assignment = assignments?.[s.id];
      if (key && assignment) {
        byKey[key] = assignment.doctorId;
        if (assignment.locked) lockedByKey[key] = true;
      }
    }
    setState(s => ({
      ...s,
      shiftSlots: normalizedSlots,
      shiftAssignments: shiftAssignmentsFlat,
      shiftAssignmentsByKey: byKey,
      lockedAssignmentsByKey: lockedByKey,
      ...(refreshMods && Array.isArray(modifications)
        ? { calendarModifications: modifications.map(normalizeCalendarModificationRow) }
        : {}),
    }));
  } catch (e) {
    console.warn('Refresh assegnazioni turni fallito:', e.message);
  }
}

/**
 * Chiave logica stabile (lgk) della cella: la `slot_key` in DB viene riscritta
 * dal server (import, bonifiche di avvio, aggancio one-off) e non coincide più
 * con quella che la UI ha in mano. Senza questa chiave le scritture per chiave
 * finiscono in 404 e l'assegnazione resta appesa nel DB.
 */
function plannerLogicalKeyFor(slot, slotKey = '') {
  const fromSlot = slot ? stableCalendarSlotKey(slot) : '';
  if (fromSlot) return fromSlot;
  const k = String(slotKey || '');
  return /^\d{4}-\d{2}-\d{2}-lgk-[0-9a-f]{8}-[0-9a-f]{8}$/.test(k) ? k : '';
}

/** Chiavi/id in stato che rappresentano la STESSA cella planner (alias + duplicati logici). */
function plannerStateAliasesForCell(state, slotKey, slot) {
  const keys = new Set();
  const ids = new Set();
  if (slotKey) keys.add(slotKey);
  const lk = slot ? slotLogicalKeyString(slot) : '';
  for (const rs of state.shiftSlots || []) {
    const kr = rs.slotKey || rs.slot_key || rs.id;
    const sameKey = (kr && kr === slotKey) || (rs.id && rs.id === slotKey);
    const sameLogical = Boolean(lk) && slotLogicalKeyString(_slotShapeForLogicalKey(rs)) === lk;
    if (!sameKey && !sameLogical) continue;
    if (kr) keys.add(kr);
    if (rs.id) ids.add(rs.id);
  }
  return { keys: [...keys], ids: [...ids] };
}

/** ID riga DB per slotKey: allinea shiftAssignments[id] a shiftAssignmentsByKey (conteggi ore: prima byKey). */
function getPlannerShiftSlotId(state, slotKey, slot) {
  if (slot?.id) return slot.id;
  if (slotKey == null || slotKey === '') return null;
  const sk = String(slotKey);
  for (const r of state.shiftSlots || []) {
    if (!r?.id) continue;
    if (r.slotKey != null && String(r.slotKey) === sk) return r.id;
  }
  return null;
}

/**
 * Assegna un medico allo slot per slotKey. Se lo slot non esiste nel DB (404),
 * lo crea prima e poi assegna, così le assegnazioni manuali persistono sempre.
 * @param {string} slotKey
 * @param {string} doctorId
 * @param {object} [slot] - slot completo (date, timeSlot, category, ecc.) per crearlo se manca
 */
export async function assignDoctorToSlotByKey(slotKey, doctorId, slot = null, locked = true, persistOpts = {}) {
  let persisted = false;
  let createdSlotId = null;
  const opts = { ...persistOpts };
  const logicalKey = plannerLogicalKeyFor(slot, slotKey);
  if (logicalKey) opts.logicalKey = logicalKey;
  try {
    await api.apiSetAssignmentByKey(slotKey, doctorId, locked, opts);
    persisted = true;
  } catch (e) {
    if (e.message?.includes('404') && slot) {
      try {
        createdSlotId = await api.apiCreateShiftSlot(slot);
        // La POST può riusare una riga canonica già presente con slot_key
        // diversa: si prosegue per id, altrimenti la set-by-key torna 404.
        if (createdSlotId) await api.apiSetAssignment(createdSlotId, doctorId, locked, opts);
        else await api.apiSetAssignmentByKey(slotKey, doctorId, locked, opts);
        persisted = true;
      } catch (e2) {
        console.warn('Creazione slot e assegnazione fallita:', e2.message);
        throw e2;
      }
    } else {
      console.warn('Persistenza assegnazione fallita:', e.message);
      throw e;
    }
  }
  if (!persisted) {
    throw new Error('Persistenza assegnazione fallita');
  }
  setState(s => {
    const slotId = createdSlotId || getPlannerShiftSlotId(s, slotKey, slot);
    const nextShift = { ...s.shiftAssignments };
    const nextLockedById = { ...s.lockedAssignments };
    const nextByKey = { ...s.shiftAssignmentsByKey };
    const nextLockedByKey = { ...s.lockedAssignmentsByKey };
    // Alias della stessa cella (slot_key riscritte lato server): vanno riallineati,
    // altrimenti il fallback per chiave logica continua a leggere il vecchio medico.
    const alias = plannerStateAliasesForCell(s, slotKey, slot);
    for (const k of alias.keys) {
      delete nextByKey[k];
      delete nextLockedByKey[k];
    }
    for (const id of alias.ids) {
      delete nextShift[id];
      delete nextLockedById[id];
    }
    if (slotId) {
      nextShift[slotId] = doctorId;
      nextLockedById[slotId] = locked;
    }
    nextByKey[slotKey] = doctorId;
    nextLockedByKey[slotKey] = locked;
    return {
      ...s,
      shiftAssignments: nextShift,
      lockedAssignments: nextLockedById,
      shiftAssignmentsByKey: nextByKey,
      lockedAssignmentsByKey: nextLockedByKey,
      shiftImportIssues: clearShiftImportIssuesForSlot(s.shiftImportIssues, slotKey, slot),
    };
  });
}

export async function unassignSlotByKey(slotKey, slot = null) {
  try {
    await api.apiDeleteAssignmentByKey(slotKey, plannerLogicalKeyFor(slot, slotKey));
  } catch (e) {
    // 404 = nessuna riga persistita per questa cella: non c'è nulla da rimuovere.
    // Ogni altro errore va propagato: se lo si ingoia la UI mostra la cella
    // libera mentre il DB tiene l'assegnazione, e la mossa successiva va in 409.
    if (!e.message?.includes('404')) throw e;
  }
  setState(s => {
    const next = { ...s.shiftAssignmentsByKey };
    const nextLockedByKey = { ...s.lockedAssignmentsByKey };
    const nextShift = { ...s.shiftAssignments };
    const nextLockedById = { ...s.lockedAssignments };
    const slotId = getPlannerShiftSlotId(s, slotKey, slot);
    if (slotId) {
      delete nextShift[slotId];
      delete nextLockedById[slotId];
    }
    // Pulisce tutti gli alias della cella (slot_key riscritte lato server e
    // duplicati logici): altrimenti il fallback per chiave logica continua a
    // mostrare il medico appena rimosso e i conteggi ore restano indietro.
    const alias = plannerStateAliasesForCell(s, slotKey, slot);
    for (const k of alias.keys) {
      delete next[k];
      delete nextLockedByKey[k];
    }
    for (const id of alias.ids) {
      delete nextShift[id];
      delete nextLockedById[id];
    }
    return {
      ...s,
      shiftAssignments: nextShift,
      lockedAssignments: nextLockedById,
      shiftAssignmentsByKey: next,
      lockedAssignmentsByKey: nextLockedByKey,
      shiftImportIssues: clearShiftImportIssuesForSlot(s.shiftImportIssues, slotKey, slot),
    };
  });
}

/**
 * Sposta un medico da uno slot a un altro (DnD verso cella vuota): un solo setState,
 * così il re-render non passa per uno stato intermedio con entrambe le celle vuote.
 * @param {string[]|null} [keysToClear] - chiavi da rimuovere dallo state (stessa riga / alias legacy). Se null, solo fromKey.
 *   Su API si tenta la DELETE per ogni chiave (404 ignorato); rollback errore destinazione solo su fromKey.
 * @param {object} [persistOpts] - override manuali; `logicalKeys` mappa chiave → lgk per risolvere
 *   le celle le cui slot_key sono state riscritte lato server.
 */
export async function moveAssignmentBetweenKeys(fromKey, toKey, doctorId, targetSlot = null, locked = true, keysToClear = null, persistOpts = {}) {
  const docId = doctorId != null ? String(doctorId).trim() : '';
  if (!fromKey || !toKey || !docId || fromKey === toKey) return;
  const targetOpts = {};
  if (persistOpts.allowDirezioneManualOverride) targetOpts.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoManualOverride) targetOpts.allowNightRestSmontoManualOverride = true;
  const restoreOpts = {};
  if (persistOpts.allowDirezioneManualOverrideRestore) restoreOpts.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoManualOverrideRestore) restoreOpts.allowNightRestSmontoManualOverride = true;
  const logicalKeys = persistOpts.logicalKeys || {};
  const lgkFor = (k, slot = null) => logicalKeys[k] || plannerLogicalKeyFor(slot, k);
  const targetLgk = lgkFor(toKey, targetSlot);
  if (targetLgk) targetOpts.logicalKey = targetLgk;
  const fromLgk = lgkFor(fromKey);
  if (fromLgk) restoreOpts.logicalKey = fromLgk;
  const clearKeys = Array.isArray(keysToClear) && keysToClear.length
    ? [...new Set(keysToClear)]
    : [fromKey];
  for (const k of clearKeys) {
    try {
      await api.apiDeleteAssignmentByKey(k, lgkFor(k));
    } catch (e) {
      if (!e.message?.includes('404')) console.warn('Rimozione assegnazione origine fallita:', k, e.message);
    }
  }
  try {
    await api.apiSetAssignmentByKey(toKey, docId, locked, targetOpts);
  } catch (e) {
    if (e.message?.includes('404') && targetSlot) {
      try {
        const createdId = await api.apiCreateShiftSlot(targetSlot);
        if (createdId) await api.apiSetAssignment(createdId, docId, locked, targetOpts);
        else await api.apiSetAssignmentByKey(toKey, docId, locked, targetOpts);
      } catch (e2) {
        console.warn('DnD: creazione/assegna destinazione fallita:', e2.message);
        try {
          await api.apiSetAssignmentByKey(fromKey, docId, locked, restoreOpts);
        } catch (_) {}
        throw e2;
      }
    } else {
      console.warn('DnD: assegnazione destinazione fallita:', e.message);
      try {
        await api.apiSetAssignmentByKey(fromKey, docId, locked, restoreOpts);
      } catch (_) {}
      throw e;
    }
  }
  setState(s => {
    const next = { ...s.shiftAssignmentsByKey };
    const nextLocked = { ...s.lockedAssignmentsByKey };
    const nextShift = { ...s.shiftAssignments };
    const nextLockedById = { ...s.lockedAssignments };
    let nextIssues = Array.isArray(s.shiftImportIssues) ? [...s.shiftImportIssues] : [];
    for (const k of clearKeys) {
      delete next[k];
      delete nextLocked[k];
      const clearId = getPlannerShiftSlotId(s, k, null);
      if (clearId) {
        delete nextShift[clearId];
        delete nextLockedById[clearId];
      }
      nextIssues = clearShiftImportIssuesForSlot(nextIssues, k, null);
    }
    next[toKey] = docId;
    nextLocked[toKey] = locked;
    const toId = getPlannerShiftSlotId(s, toKey, targetSlot);
    if (toId) {
      nextShift[toId] = docId;
      nextLockedById[toId] = locked;
    }
    nextIssues = clearShiftImportIssuesForSlot(nextIssues, toKey, targetSlot);
    return {
      ...s,
      shiftAssignments: nextShift,
      lockedAssignments: nextLockedById,
      shiftAssignmentsByKey: next,
      lockedAssignmentsByKey: nextLocked,
      shiftImportIssues: nextIssues,
    };
  });
}

/**
 * Scambio atomico di due assegnazioni (drag & drop swap).
 * Esegue le 3 operazioni API e aggiorna lo state una sola volta alla fine.
 */
export async function swapAssignmentsByKey(sourceSlotKey, targetSlotKey, sourceSlot, targetSlot, sourceDoctorId, targetDoctorId, locked = true, persistOpts = {}) {
  const optSrcOnTarget = {};
  if (persistOpts.allowDirezioneSourceOnTarget) optSrcOnTarget.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoSourceOnTarget) optSrcOnTarget.allowNightRestSmontoManualOverride = true;
  const optTgtOnSource = {};
  if (persistOpts.allowDirezioneTargetOnSource) optTgtOnSource.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoTargetOnSource) optTgtOnSource.allowNightRestSmontoManualOverride = true;
  const srcLgk = plannerLogicalKeyFor(sourceSlot, sourceSlotKey);
  const tgtLgk = plannerLogicalKeyFor(targetSlot, targetSlotKey);
  if (tgtLgk) optSrcOnTarget.logicalKey = tgtLgk;
  if (srcLgk) optTgtOnSource.logicalKey = srcLgk;
  try {
    await api.apiDeleteAssignmentByKey(sourceSlotKey, srcLgk);
  } catch (e) {
    // 404 = cella non persistita; ogni altro errore lascerebbe l'origine occupata
    // in DB e farebbe fallire con 409 l'assegnazione sulla destinazione.
    if (!e.message?.includes('404')) throw new Error('Rimozione assegnazione origine fallita: ' + (e.message || e));
  }
  try {
    await api.apiSetAssignmentByKey(targetSlotKey, sourceDoctorId, locked, optSrcOnTarget);
  } catch (e) {
    if (e.message?.includes('404') && targetSlot) {
      try {
        const createdTgt = await api.apiCreateShiftSlot(targetSlot);
        if (createdTgt) await api.apiSetAssignment(createdTgt, sourceDoctorId, locked, optSrcOnTarget);
        else await api.apiSetAssignmentByKey(targetSlotKey, sourceDoctorId, locked, optSrcOnTarget);
      } catch (e2) {
        throw new Error('Assegnazione destinazione fallita: ' + (e2.message || e2));
      }
    } else {
      throw new Error('Assegnazione destinazione fallita: ' + (e.message || e));
    }
  }
  try {
    await api.apiSetAssignmentByKey(sourceSlotKey, targetDoctorId, locked, optTgtOnSource);
  } catch (e) {
    if (e.message?.includes('404') && sourceSlot) {
      try {
        const createdSrc = await api.apiCreateShiftSlot(sourceSlot);
        if (createdSrc) await api.apiSetAssignment(createdSrc, targetDoctorId, locked, optTgtOnSource);
        else await api.apiSetAssignmentByKey(sourceSlotKey, targetDoctorId, locked, optTgtOnSource);
      } catch (e2) {
        throw new Error('Assegnazione origine fallita: ' + (e2.message || e2));
      }
    } else {
      throw new Error('Assegnazione origine fallita: ' + (e.message || e));
    }
  }
  setState(s => {
    const nextShift = { ...s.shiftAssignments };
    const nextLockedById = { ...s.lockedAssignments };
    const srcId = getPlannerShiftSlotId(s, sourceSlotKey, sourceSlot);
    const tgtId = getPlannerShiftSlotId(s, targetSlotKey, targetSlot);
    if (srcId) {
      nextShift[srcId] = targetDoctorId;
      nextLockedById[srcId] = locked;
    }
    if (tgtId) {
      nextShift[tgtId] = sourceDoctorId;
      nextLockedById[tgtId] = locked;
    }
    return {
      ...s,
      shiftAssignments: nextShift,
      lockedAssignments: nextLockedById,
      shiftAssignmentsByKey: {
        ...s.shiftAssignmentsByKey,
        [sourceSlotKey]: targetDoctorId,
        [targetSlotKey]: sourceDoctorId,
      },
      lockedAssignmentsByKey: {
        ...s.lockedAssignmentsByKey,
        [sourceSlotKey]: locked,
        [targetSlotKey]: locked,
      },
      shiftImportIssues: clearShiftImportIssuesForSlot(
        clearShiftImportIssuesForSlot(s.shiftImportIssues, sourceSlotKey, sourceSlot),
        targetSlotKey,
        targetSlot
      ),
    };
  });
}

/**
 * Sostituisce il piano turni con slot da Calendario + assegnazioni
 * Usa shiftAssignmentsByKey (slotKey -> doctorId), non shiftSlots
 * Mantiene assegnazioni di altri mesi (merge per mese)
 * NON cancella né ricrea gli slot con lucchetto (restano intoccabili)
 */
/** Ordine persistenza: stesso giorno, notte clinica prima dei diurni (coerenza API regola 9). */
function sortSlotsPersistOrder(slots) {
  const bandRank = (ts) => {
    const t = String(ts || '').toLowerCase();
    if (t === 'notte') return 0;
    if (t === 'mattino') return 1;
    if (t === 'pomeriggio') return 2;
    if (t === 'prolungato') return 3;
    return 9;
  };
  return [...(slots || [])].sort((a, b) => {
    const c = String(a.date || '').localeCompare(String(b.date || ''));
    if (c !== 0) return c;
    return bandRank(a.timeSlot) - bandRank(b.timeSlot);
  });
}

function _slotPhaseScopeMatch(slot, phaseScope) {
  if (!phaseScope) return true;
  const ts = String(slot?.timeSlot || slot?.time_slot || '').toLowerCase();
  const wd = String(slot?.weekday || '').toLowerCase();
  const at = String(slot?.activityType || slot?.activity_type || '').toLowerCase();
  // Replica logica getSlotPhase del solver Python.
  if (at === 'reperibilita' || ts.startsWith('reperibilita')) return phaseScope === 'reperibilita';
  if (wd === 'sab' || wd === 'dom') return phaseScope === 'weekend';
  if (ts === 'notte') return phaseScope === 'notti';
  return phaseScope === 'infrasettimanali';
}

export async function setShiftPlanBulk(slots, assignments, lockedStatus = {}, autoUnlockedSlotIds = [], options = {}) {
  const orderedSlots = sortSlotsPersistOrder(slots);
  const state = getState();
  const lockedWeeksMap = state.lockedWeeks || {};
  const stateAssignmentsByKey = state.shiftAssignmentsByKey || {};
  const stateLockedByKey = state.lockedAssignmentsByKey || {};
  const stateSlots = state.shiftSlots || [];
  const autoUnlocked = new Set(autoUnlockedSlotIds || []);
  // FIX BUG: phaseScope limita la cancellazione/clearing agli slot della sola fase indicata.
  // Senza questo, eseguire solver di una singola fase (es. reperibilita) cancellava anche
  // gli slot delle altre fasi (weekend/notti/infra) non lockati.
  const phaseScope = options && options.phase ? String(options.phase).toLowerCase() : null;
  const byKey = {};
  const lockedByKey = {};
  const chosenByDoctorDateSlot = {};
  const slotDates = new Set();
  const lockedSlotKeys = new Set();
  const lockedLogicalKeys = new Set();
  for (const slot of orderedSlots) {
    const key = slot.slotKey || slot.id;
    const isAutoUnlocked = autoUnlocked.has(key) || autoUnlocked.has(slot.id);
    const isLockedInState = !isAutoUnlocked && _shiftPlanSlotLockedViaState(
      slot,
      stateLockedByKey,
      stateSlots,
      autoUnlocked,
    );
    const hasNewAssignment = Object.prototype.hasOwnProperty.call(assignments, slot.id)
      || Object.prototype.hasOwnProperty.call(assignments, key);
    const docId = isLockedInState
      ? _shiftPlanAssignmentViaState(slot, stateAssignmentsByKey, stateSlots)
      : (
        assignments[slot.id]
        ?? assignments[key]
        ?? (!isAutoUnlocked && !hasNewAssignment
          ? _shiftPlanAssignmentViaState(slot, stateAssignmentsByKey, stateSlots)
          : undefined)
      );
    if (docId) {
      const isLockedNow = !isAutoUnlocked && (lockedStatus[slot.id] || isLockedInState);
      const clashKey = `${docId}|${slot.date || ''}|${slot.timeSlot || ''}`;
      const existing = chosenByDoctorDateSlot[clashKey];
      if (!existing) {
        byKey[key] = docId;
        if (isLockedNow) {
          lockedByKey[key] = true;
          lockedSlotKeys.add(key);
          const lk = slotLogicalKeyString(slot);
          if (lk) lockedLogicalKeys.add(lk);
        }
        chosenByDoctorDateSlot[clashKey] = { slotKey: key, locked: isLockedNow };
      } else if (isLockedNow && !existing.locked) {
        // Se c'e' conflitto nella stessa fascia, il lock coerente ha priorita'.
        delete byKey[existing.slotKey];
        delete lockedByKey[existing.slotKey];
        lockedSlotKeys.delete(existing.slotKey);
        byKey[key] = docId;
        lockedByKey[key] = true;
        lockedSlotKeys.add(key);
        const lk = slotLogicalKeyString(slot);
        if (lk) lockedLogicalKeys.add(lk);
        chosenByDoctorDateSlot[clashKey] = { slotKey: key, locked: true };
      }
    }
    if (slot.date) slotDates.add(slot.date);
  }
  for (const rs of stateSlots) {
    if (!rs?.date || !slotDates.has(rs.date)) continue;
    const rowKey = String(rs.slotKey ?? rs.id ?? '').trim();
    if (!rowKey || !stateLockedByKey[rowKey]) continue;
    const lkRow = slotLogicalKeyString(_slotShapeForLogicalKey(rs));
    if (lkRow) lockedLogicalKeys.add(lkRow);
  }
  const months = [...new Set([...slotDates].map(d => d.slice(0, 7)))];
  for (const month of months) {
    const existingSlots = await api.apiGetShiftSlots(month);
    for (const s of existingSlots || []) {
      if (!slotDates.has(s.date)) continue;
      if (lockedWeeksMap[getWeekStart(s.date)]) continue;
      const key = s.slotKey ?? s.slot_key ?? null;
      if (key && lockedSlotKeys.has(key)) continue; // Non cancellare slot con lucchetto
      const lkPersisted = slotLogicalKeyString({
        date: s.date,
        timeSlot: s.timeSlot ?? s.time_slot,
        category: s.category,
        activityType: s.activityType ?? s.activity_type,
        uocId: s.uocId ?? s.uoc_id,
        name: s.name,
      });
      if (lkPersisted && lockedLogicalKeys.has(lkPersisted)) continue;
      // Fase singola: non cancellare slot di OTHER fasi (mantieni weekend/notti/infra
      // quando si lancia solo la fase reperibilita, e viceversa).
      if (phaseScope && !_slotPhaseScopeMatch(s, phaseScope)) continue;
      try { await api.apiDeleteShiftSlot(s.id); } catch (_) {}
    }
  }
  for (const slot of orderedSlots) {
    const key = slot.slotKey || slot.id;
    if (lockedSlotKeys.has(key)) continue; // Non ricreare: slot già presente e intoccabile
    if (slot.date && lockedWeeksMap[getWeekStart(slot.date)]) continue;
    const newId = await api.apiCreateShiftSlot(slot);
    const docId = byKey[key];
    if (docId) {
      const isLocked = lockedByKey[key] || false;
      await api.apiSetAssignment(newId, docId, isLocked);
    }
  }
  setState(s => {
    const lw = s.lockedWeeks || {};
    const existing = { ...s.shiftAssignmentsByKey };
    const existingLocked = { ...s.lockedAssignmentsByKey };
    for (const k of Object.keys(existing)) {
      const datePart = k.slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) continue;
      if (!slotDates.has(datePart)) continue;
      if (lw[getWeekStart(datePart)]) continue;
      // Fase singola: non rimuovere dallo state le assegnazioni di OTHER fasi.
      if (phaseScope) {
        const stateSlot = stateSlots.find(ss => String(ss.slotKey ?? ss.slot_key ?? ss.id ?? '').trim() === k);
        if (stateSlot && !_slotPhaseScopeMatch(stateSlot, phaseScope)) continue;
      }
      delete existing[k];
      delete existingLocked[k];
    }
    return {
      ...s,
      shiftSlots: [],
      shiftAssignments: {},
      shiftAssignmentsByKey: { ...existing, ...byKey },
      lockedAssignmentsByKey: { ...existingLocked, ...lockedByKey },
    };
  });
}

/** Salva il carico di lavoro di un mese nello storico (stato + backend) */
export async function saveWorkloadToHistory(monthKey, workload) {
  try {
    await api.apiSaveWorkloadMonth(monthKey, workload);
  } catch (e) {
    console.warn('Salvataggio workload su backend fallito:', e.message);
  }
  setState(s => ({
    ...s,
    workloadHistory: { ...s.workloadHistory, [monthKey]: workload },
  }));
}

/** Rimuove un mese dallo storico carichi (stato + backend). */
export async function deleteWorkloadMonthFromHistory(monthKey) {
  try {
    await api.apiDeleteWorkloadMonth(monthKey);
  } catch (e) {
    console.warn('Rimozione storico carichi fallita:', e.message);
  }
  setState(s => {
    const next = { ...(s.workloadHistory || {}) };
    delete next[monthKey];
    return { ...s, workloadHistory: next };
  });
}

function scheduleWorkloadHistorySync(weekStart) {
  queueMicrotask(() => {
    void import('./workload.js')
      .then(m => m.syncWorkloadHistoryAfterWeekLockChange(weekStart))
      .catch(e => console.warn('Sincronizzazione storico carichi:', e?.message || e));
  });
}

/**
 * Consolida un bimestre: fotografa il monte orario com'e' alla chiusura.
 * Serve perche' i debiti residui producono ritenute in busta paga: il dato deve restare
 * verificabile anche se il piano viene modificato dopo.
 */
export async function saveBimesterSnapshot(bimesterKey, data) {
  await api.apiSaveBimesterSnapshot(bimesterKey, data);
  const snapshot = { data, createdAt: new Date().toISOString(), createdBy: state.currentUser?.username ?? null };
  setState(s => ({
    ...s,
    workloadBimesterSnapshots: { ...(s.workloadBimesterSnapshots || {}), [bimesterKey]: snapshot },
  }));
  return snapshot;
}

/** Rimuove il consolidamento di un bimestre (stato + backend). */
export async function deleteBimesterSnapshot(bimesterKey) {
  await api.apiDeleteBimesterSnapshot(bimesterKey);
  setState(s => {
    const next = { ...(s.workloadBimesterSnapshots || {}) };
    delete next[bimesterKey];
    return { ...s, workloadBimesterSnapshots: next };
  });
}

/** Consolidamenti bimestrali: { "YYYY-Bn": { data, createdAt, createdBy } } */
export function getBimesterSnapshots() {
  return state.workloadBimesterSnapshots || {};
}

/** Restituisce lo storico carichi per mese */
export function getWorkloadHistory() {
  return state.workloadHistory || {};
}

/** Blocca una settimana (archiviazione). weekStart = YYYY-MM-DD (lunedì) */
export async function lockWeek(weekStart) {
  let resp = null;
  try {
    resp = await api.apiLockWeek(weekStart);
  } catch (e) {
    console.warn('Blocco settimana fallito:', e.message);
    throw e;
  }
  setState(s => ({ ...s, lockedWeeks: { ...s.lockedWeeks, [weekStart]: true } }));
  scheduleWorkloadHistorySync(weekStart);
  return resp;
}

/** Sblocca una settimana */
export async function unlockWeek(weekStart) {
  try {
    await api.apiUnlockWeek(weekStart);
  } catch (e) {
    console.warn('Sblocco settimana fallito:', e.message);
    throw e;
  }
  setState(s => {
    const next = { ...s.lockedWeeks };
    delete next[weekStart];
    return { ...s, lockedWeeks: next };
  });
  scheduleWorkloadHistorySync(weekStart);
}

/** True se la settimana è bloccata (archiviata) */
export function isWeekLocked(weekStart) {
  return Boolean(state.lockedWeeks?.[weekStart]);
}

/** Blocco richieste assenze/desiderata per utenti medico (stesura turni) */
export async function lockAbsenceRequestWeek(weekStart) {
  try {
    await api.apiLockAbsenceRequestWeek(weekStart);
  } catch (e) {
    console.warn('Blocco richieste assenza settimana fallito:', e.message);
    throw e;
  }
  setState(s => ({ ...s, absenceRequestLockedWeeks: { ...s.absenceRequestLockedWeeks, [weekStart]: true } }));
}

export async function unlockAbsenceRequestWeek(weekStart) {
  try {
    await api.apiUnlockAbsenceRequestWeek(weekStart);
  } catch (e) {
    console.warn('Sblocco richieste assenza settimana fallito:', e.message);
    throw e;
  }
  setState(s => {
    const next = { ...s.absenceRequestLockedWeeks };
    delete next[weekStart];
    return { ...s, absenceRequestLockedWeeks: next };
  });
}

/** Pulisce tutte le assegnazioni del mese indicato (YYYY-MM) - stato e backend */
export async function clearShiftPlanForMonth(monthKey) {
  try {
    const existingSlots = await api.apiGetShiftSlots(monthKey);
    for (const s of existingSlots || []) {
      try { await api.apiDeleteShiftSlot(s.id); } catch (_) {}
    }
  } catch (e) {
    console.warn('Pulizia backend fallita:', e.message);
  }
  setState(s => {
    const next = { ...s.shiftAssignmentsByKey };
    for (const k of Object.keys(next)) {
      if (k.startsWith(monthKey + '-')) delete next[k];
    }
    return { ...s, shiftAssignmentsByKey: next };
  });
}

/** Pulisce le assegnazioni nell'intervallo date (YYYY-MM-DD) - stato e backend.
 *  Non tocca i turni con lucchetto (locked) né le date in settimane archiviate (lockedWeeks).
 *
 *  Svuota le celle ma NON rimuove slot né modifiche calendario: la pianificazione
 *  settimanale (slot previsti e relativi carichi) sopravvive alla pulizia. Vale in
 *  particolare per gli slot aggiunti da un'importazione PDF/Excel che il Calendario
 *  non prevedeva: restano visibili e riassegnabili invece di sparire dal piano.
 */
export async function clearShiftPlanForDateRange(startDate, endDate) {
  const dates = new Set();
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  const cur = new Date(start);
  while (cur <= end) {
    dates.add(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  const lockedByKey = getState().lockedAssignmentsByKey || {};
  const lockedWeeks = getState().lockedWeeks || {};
  const datesToClear = [...dates].filter(d => !lockedWeeks[getWeekStart(d)]);
  const months = [...new Set([...datesToClear].map(d => d.slice(0, 7)))];
  // Pulisce le ASSEGNAZIONI, non la pianificazione: slot e modifiche calendario
  // restano. Un'importazione da PDF/Excel puo' aggiungere slot non previsti a
  // calendario; sono pianificazione della settimana e devono sopravvivere alla
  // pulizia, altrimenti si perderebbero di vista i carichi di lavoro previsti.
  try {
    const assignments = await api.apiGetAssignments();
    for (const month of months) {
      const existingSlots = await api.apiGetShiftSlots(month);
      for (const s of existingSlots || []) {
        if (!datesToClear.includes(s.date)) continue;
        const assignment = assignments?.[s.id];
        if (!assignment) continue; // cella gia' vuota: niente da pulire
        if (assignment.locked) continue; // lucchetto lato server
        const key = s.slotKey ?? s.slot_key ?? null;
        if (key && lockedByKey[key]) continue; // lucchetto noto al client
        try { await api.apiDeleteAssignment(s.id); } catch (_) {}
      }
    }
  } catch (e) {
    console.warn('Pulizia backend fallita:', e.message);
  }
  setState(s => {
    const nextAssignments = { ...s.shiftAssignmentsByKey };
    const nextLocked = { ...s.lockedAssignmentsByKey };
    for (const k of Object.keys(nextAssignments)) {
      const datePart = k.slice(0, 10);
      if (datesToClear.includes(datePart) && !lockedByKey[k]) delete nextAssignments[k];
      if (datesToClear.includes(datePart) && !lockedByKey[k]) delete nextLocked[k];
    }
    return { ...s, shiftAssignmentsByKey: nextAssignments, lockedAssignmentsByKey: nextLocked };
  });
}
